import { useEffect, useMemo, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Button, Text, TextField } from "@radix-ui/themes";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { entityService } from "@/services/breeze/EntityService";
import { IEntity } from "@/types/breeze/Entity";
import { Field } from "@/types/breeze/Field";

interface Props {
  entityName: string;
  value: string | null | undefined;
  onChange: (path: string | null) => void;
  placeholder?: string;
  clearable?: boolean;
  width?: number;
  maxDepth?: number;
  allowRelationshipAsLeaf?: boolean;
  fieldFilter?: (field: Field) => boolean;
}

export default function RelationshipFieldPicker({
  entityName,
  value,
  onChange,
  placeholder = "Field",
  clearable = true,
  width = 220,
  maxDepth = 3,
  allowRelationshipAsLeaf = false,
  fieldFilter,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const label = value ? formatPath(value) : placeholder;
  const loadedEntities = useEntityGraph(entityName, maxDepth);

  const flatOptions = useMemo(() => {
    if (!search) return [];
    return buildFlatOptions(entityName, loadedEntities, maxDepth, allowRelationshipAsLeaf, fieldFilter)
      .filter((o) => {
        const q = search.toLowerCase();
        return o.value.toLowerCase().includes(q) || o.label.toLowerCase().includes(q);
      });
  }, [search, entityName, loadedEntities, maxDepth, allowRelationshipAsLeaf, fieldFilter]);

  const pick = (path: string) => {
    onChange(path);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setTimeout(() => inputRef.current?.focus(), 0);
        else setSearch("");
      }}
    >
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="soft"
          color={value ? undefined : "gray"}
          style={{ width, justifyContent: "space-between", fontWeight: "normal" }}
        >
          <Text truncate>{label}</Text>
          <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
            {clearable && value && (
              <X
                size={12}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange(null);
                }}
              />
            )}
            <ChevronDown size={12} />
          </span>
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        sideOffset={4}
        style={{
          width: Math.max(width, 360),
          padding: 0,
          zIndex: 12000,
          backgroundColor: "var(--color-panel-solid)",
          border: "1px solid var(--gray-a5)",
          borderRadius: "var(--radius-3)",
          boxShadow: "var(--shadow-4)",
        }}
      >
        <div style={{ padding: 8, borderBottom: "1px solid var(--gray-5)" }}>
          <TextField.Root
            ref={inputRef}
            placeholder="Search fields…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="2"
          />
        </div>
        <div
          style={{ maxHeight: 320, overflowY: "auto", overflowX: "hidden", padding: 4 }}
          onWheel={(e) => {
            e.currentTarget.scrollTop += e.deltaY;
            e.stopPropagation();
          }}
        >
          {search ? (
            flatOptions.length === 0 ? (
              <Text size="2" color="gray" style={{ display: "block", padding: 8 }}>No matches</Text>
            ) : (
              flatOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => pick(opt.value)}
                  style={{
                    ...leafButtonStyle(0),
                    fontWeight: opt.value === value ? 600 : 400,
                    backgroundColor: opt.value === value ? "var(--accent-3)" : undefined,
                  }}
                >
                  {opt.label}
                </button>
              ))
            )
          ) : (
            <TreeLevel
              entityName={entityName}
              loadedEntities={loadedEntities}
              pathPrefix=""
              depth={0}
              maxDepth={maxDepth}
              visited={new Set([entityName])}
              allowRelationshipAsLeaf={allowRelationshipAsLeaf}
              fieldFilter={fieldFilter}
              value={value ?? null}
              onPick={pick}
            />
          )}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

function TreeLevel({
  entityName,
  loadedEntities,
  pathPrefix,
  depth,
  maxDepth,
  visited,
  allowRelationshipAsLeaf,
  fieldFilter,
  value,
  onPick,
}: {
  entityName: string;
  loadedEntities: Map<string, IEntity>;
  pathPrefix: string;
  depth: number;
  maxDepth: number;
  visited: Set<string>;
  allowRelationshipAsLeaf: boolean;
  fieldFilter?: (f: Field) => boolean;
  value: string | null;
  onPick: (path: string) => void;
}) {
  const entity = loadedEntities.get(entityName);
  if (!entity) {
    return (
      <div style={{ padding: "6px 10px" }}>
        <Text size="1" color="gray">Loading {entityName}…</Text>
      </div>
    );
  }
  const relMap = buildRelationshipMap(entity);

  return (
    <>
      {(entity.fields ?? [])
        .filter((f) => !f.relationship_id || relMap.has(f.name))
        .filter((f) => (fieldFilter ? fieldFilter(f) : true))
        .map((field) => (
          <FieldNode
            key={field.id ?? field.name}
            field={field}
            relatedEntityName={relMap.get(field.name)}
            pathPrefix={pathPrefix}
            depth={depth}
            maxDepth={maxDepth}
            visited={visited}
            allowRelationshipAsLeaf={allowRelationshipAsLeaf}
            fieldFilter={fieldFilter}
            loadedEntities={loadedEntities}
            value={value}
            onPick={onPick}
          />
        ))}
    </>
  );
}

function FieldNode({
  field,
  relatedEntityName,
  pathPrefix,
  depth,
  maxDepth,
  visited,
  allowRelationshipAsLeaf,
  fieldFilter,
  loadedEntities,
  value,
  onPick,
}: {
  field: Field;
  relatedEntityName?: string;
  pathPrefix: string;
  depth: number;
  maxDepth: number;
  visited: Set<string>;
  allowRelationshipAsLeaf: boolean;
  fieldFilter?: (f: Field) => boolean;
  loadedEntities: Map<string, IEntity>;
  value: string | null;
  onPick: (path: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const fullPath = pathPrefix ? `${pathPrefix}.${field.name}` : field.name;
  const canDrill = !!relatedEntityName && depth + 1 < maxDepth && !visited.has(relatedEntityName);
  const isSelected = value === fullPath;

  if (canDrill) {
    const nextVisited = new Set(visited).add(relatedEntityName!);
    return (
      <div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={branchButtonStyle(depth)}
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <span style={{ marginLeft: 4 }}>{field.label || field.name}</span>
          <Text size="1" color="gray" style={{ marginLeft: 6 }}>
            → {relatedEntityName}
          </Text>
        </button>
        {expanded && (
          <div style={{ paddingLeft: 12, borderLeft: "1px solid var(--gray-4)", marginLeft: 8 }}>
            {allowRelationshipAsLeaf && (
              <button
                type="button"
                onClick={() => onPick(fullPath)}
                style={{
                  ...leafButtonStyle(depth + 1),
                  fontWeight: value === fullPath ? 600 : 400,
                  backgroundColor: value === fullPath ? "var(--accent-3)" : undefined,
                }}
              >
                <Text size="1" color="gray">Insert {field.name} directly</Text>
              </button>
            )}
            <TreeLevel
              entityName={relatedEntityName!}
              loadedEntities={loadedEntities}
              pathPrefix={fullPath}
              depth={depth + 1}
              maxDepth={maxDepth}
              visited={nextVisited}
              allowRelationshipAsLeaf={allowRelationshipAsLeaf}
              fieldFilter={fieldFilter}
              value={value}
              onPick={onPick}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onPick(fullPath)}
      style={{
        ...leafButtonStyle(depth),
        fontWeight: isSelected ? 600 : 400,
        backgroundColor: isSelected ? "var(--accent-3)" : undefined,
      }}
    >
      <span>{field.label || field.name}</span>
      {field.name !== (field.label ?? field.name) && (
        <Text size="1" color="gray" style={{ marginLeft: 6 }}>
          {field.name}
        </Text>
      )}
    </button>
  );
}

function formatPath(path: string): string {
  return path.split(".").join(" → ");
}

function branchButtonStyle(depth: number): React.CSSProperties {
  return {
    all: "unset",
    display: "flex",
    alignItems: "center",
    padding: "6px 8px",
    paddingLeft: 8 + depth * 4,
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
    fontSize: 13,
    borderRadius: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
}

function leafButtonStyle(depth: number): React.CSSProperties {
  return {
    all: "unset",
    display: "flex",
    alignItems: "baseline",
    padding: "6px 8px",
    paddingLeft: 8 + depth * 4,
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
    fontSize: 13,
    borderRadius: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
}

function buildRelationshipMap(entity: IEntity): Map<string, string> {
  const map = new Map<string, string>();
  for (const rel of entity.source_relationships ?? []) {
    if (rel.type !== "many-to-one" && rel.type !== "one-to-one") continue;
    if (!rel.source_field_name || !rel.target_entity_name) continue;
    map.set(rel.source_field_name, rel.target_entity_name);
  }
  return map;
}

function useEntityGraph(rootEntity: string, maxDepth: number): Map<string, IEntity> {
  const { data } = useQuery({
    queryKey: ["entities-all", { includeSystem: true }],
    queryFn: () => entityService.getAll(true),
    staleTime: 5 * 60 * 1000,
  });

  const baseMap = useMemo(() => {
    const map = new Map<string, IEntity>();
    for (const e of data ?? []) map.set(e.name, e);
    return map;
  }, [data]);

  const [fetched, setFetched] = useState<Map<string, IEntity>>(new Map());

  const currentMap = useMemo(() => {
    const map = new Map(baseMap);
    fetched.forEach((v, k) => map.set(k, v));
    return map;
  }, [baseMap, fetched]);

  const missingNames = useMemo(() => {
    const wanted = new Set<string>();
    const walk = (name: string, depth: number, visited: Set<string>) => {
      const entity = currentMap.get(name);
      if (!entity || depth >= maxDepth) return;
      for (const rel of entity.source_relationships ?? []) {
        if (rel.type !== "many-to-one" && rel.type !== "one-to-one") continue;
        if (!rel.target_entity_name || visited.has(rel.target_entity_name)) continue;
        if (!currentMap.has(rel.target_entity_name)) wanted.add(rel.target_entity_name);
        walk(rel.target_entity_name, depth + 1, new Set(visited).add(rel.target_entity_name));
      }
    };
    walk(rootEntity, 0, new Set([rootEntity]));
    return Array.from(wanted);
  }, [currentMap, rootEntity, maxDepth]);

  const detailQueries = useQueries({
    queries: missingNames.map((name) => ({
      queryKey: ["entity", name, { includeSystem: true }],
      queryFn: () => entityService.getByName(name, true),
      staleTime: 5 * 60 * 1000,
    })),
  });

  useEffect(() => {
    let next: Map<string, IEntity> | null = null;
    for (const q of detailQueries) {
      if (!q.data) continue;
      if (fetched.get(q.data.name) === q.data) continue;
      next ??= new Map(fetched);
      next.set(q.data.name, q.data);
    }
    if (next) setFetched(next);
  }, [detailQueries, fetched]);

  return currentMap;
}

interface FlatOption {
  value: string;
  label: string;
}

function buildFlatOptions(
  rootEntity: string,
  loadedEntities: Map<string, IEntity>,
  maxDepth: number,
  allowRelationshipAsLeaf: boolean,
  fieldFilter?: (f: Field) => boolean,
): FlatOption[] {
  const out: FlatOption[] = [];
  const walk = (entityName: string, pathPrefix: string, labelPrefix: string, depth: number, visited: Set<string>) => {
    const entity = loadedEntities.get(entityName);
    if (!entity) return;
    const relMap = buildRelationshipMap(entity);
    for (const field of entity.fields ?? []) {
      if (fieldFilter && !fieldFilter(field)) continue;
      const related = relMap.get(field.name);
      const fullPath = pathPrefix ? `${pathPrefix}.${field.name}` : field.name;
      const fullLabel = labelPrefix ? `${labelPrefix} → ${field.label || field.name}` : field.label || field.name;
      if (related) {
        if (allowRelationshipAsLeaf) out.push({ value: fullPath, label: fullLabel });
        if (depth + 1 < maxDepth && !visited.has(related)) {
          walk(related, fullPath, fullLabel, depth + 1, new Set(visited).add(related));
        }
      } else if (!field.relationship_id) {
        out.push({ value: fullPath, label: fullLabel });
      }
    }
  };
  walk(rootEntity, "", "", 0, new Set([rootEntity]));
  return out;
}
